import $ from 'jquery';
import _ from 'underscore';
import { decorate } from '@bingads-webui/dialog';
import { focusLockInstanceDecorator } from '@bingads-webui/focus-lock-decorator';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { LabelsSelectorView, CreateLabelView, create, navigateToManagePage } from '@bingads-webui-campaign/labels';

function getCheckMap(selectionInfo) {
  if (selectionInfo && selectionInfo.selectedItems) {
    const totalCount = selectionInfo.selectedRowIds.length;

    _.each(selectionInfo.selectedItems, (selectedItem) => {
      _.each(selectedItem.Labels, (label) => {
        if (typeof JSON.parse(label.Id) !== 'object' && label.Scope) {
          // eslint-disable-next-line no-param-reassign
          label.Id = JSON.stringify({ Id: label.Id, Scope: label.Scope });
        }
      });
    });

    // first got the ids of labels for each selected items
    // then got the count for each id by _.countby(_.flatten)
    const labelsCount = _.countBy(_.flatten(_.map(
      selectionInfo.selectedItems,
      rowItem => _.pluck(rowItem.Labels || [], 'Id')
    )));

    // finally map each label id to the different state based on whether its all checked
    const result = {};
    let isShortCycle = true;
    _.each(labelsCount, (count, id) => {
      if (count === totalCount) {
        result[id] = {
          Id: id,
          state: LabelsSelectorView.CheckState.checked,
          transition: LabelsSelectorView.CheckTransition.indeterminateToCheckedShortCycle,
        };
      } else {
        isShortCycle = false;
        result[id] = {
          Id: id,
          state: LabelsSelectorView.CheckState.indeterminate,
          transition: LabelsSelectorView.CheckTransition.indeterminateToCheckedFullCycle,
        };
      }
    });

    return {
      checkMap: result,
      isShortCycle,
    };
  }

  return {
    checkMap: null,
  };
}

function updateAssociation({
  grid,
  selectionInfo,
  LabelAssociationView,
  isForVNext,
  toolbarEditor,
  toolbar,
  disableDeselectAll,
  isManagementRequest,
  gridName,
}) {
  return (_action, stats) => {
    const associationView = new LabelAssociationView({
      stats,
      selectionInfo,
      grid,
      isForVNext,
      disableDeselectAll,
      isManagementRequest,
      gridName,
    });

    associationView.updateSelectedEntityCount(selectionInfo.selectedRowCount);

    associationView.render();

    associationView.once('hide', () => {
      if (isForVNext) {
        toolbarEditor.overrideWith(null);
      }
      associationView.remove();

      toolbarEditor.restoreToolbar();
      toolbar.gridToolbarGroup.showAllToolbars();
    });

    if (isForVNext) {
      toolbarEditor.overrideWith(associationView);
    } else {
      const $bulkEditContainer = $('#bulkEditContainer');
      $bulkEditContainer.html(associationView.el).show();
    }

    // make labels column selected if it is not currently
    // fetch list of names this way since query does not
    // always get patched with list of selected columns
    // did this after update so that grid will show latest labels
    grid.gridView.once('didUpdate', () => {
      const columns = _.map(grid.gridView.get('columns'), ({ name }) => name);

      if (!_.contains(columns, 'Labels')) {
        columns.push('Labels');
        grid.pgridPlugins.columnChooser.setSelectedColumns(columns);
      }
    });
  };
}

export function onCreate({
  grid,
  selectionInfo,
  i18n,
  i18nErrors,
  LabelAssociationView,
  s$label,
  instrumentation,
  jsdata,
  isForVNext,
  toolbarEditor,
  toolbar,
  disableDeselectAll,
  edm,
  scope,
  customerLevelLabelEnabled,
  isManagementRequest,
  gridName,
}) {
  return () => {
    const CreateView = decorate(CreateLabelView, {
      title: i18n.getString('create_label_dialog_title'),
      position: 'center',
      popupFront: '.navbar-fixed-top',
      closeButtonAriaLabel: i18n.getString('Close'),
    });
    const createView = new CreateView({
      i18n, s$label, i18nErrors, isForVNext,
    }).render();

    createView.on('create', (label) => {
      create({
        jsdata,
        label,
        entityType: selectionInfo.entityType,
        instrumentation,
        edm,
        scope,
      })
        .then((results) => {
          const labelId = results.value;
          const stats = {
            added: {
              [`${labelId}`]: LabelsSelectorView.CheckState.checked,
            },
          };
          if (customerLevelLabelEnabled) {
            const id = JSON.stringify({ Id: labelId, Scope: selectionInfo.entityType === 'Account' ? 'Customer' : 'Account' });
            stats.added = { [id]: { id, state: LabelsSelectorView.CheckState.checked } };
          }
          updateAssociation({
            grid,
            selectionInfo,
            LabelAssociationView,
            isForVNext,
            toolbarEditor,
            toolbar,
            disableDeselectAll,
            isManagementRequest,
            gridName,
          })('apply', stats);
        })
        .then(() => createView.remove())
        .catch((res) => {
        // todo: this silently fails if the error is not from the server
          createView.setErrors(res.errors);
        })
        .finally(withUiBlock(createView.$el));
    });

    createView.on('cancel', () => createView.remove());
  };
}

export function createLabelSelectorView({
  selectionInfo,
  grid,
  i18n,
  i18nErrors,
  router,
  LabelAssociationView,
  dataContextManager,
  cacheService,
  jsdata,
  s$label,
  instrumentation,
  isForVNext,
  toolbarEditor,
  toolbar,
  edm,
  scope,
  disableDeselectAll,
  customerLevelLabelEnabled,
  isManagementRequest,
  gridName,
  perfMarker,
  useSubMenu,
}) {
  const { checkMap, isShortCycle } = getCheckMap(selectionInfo);

  let checkAll;
  if (selectionInfo && selectionInfo.allPages) {
    checkAll = LabelsSelectorView.CheckState.indeterminate;
  } else {
    checkAll = null;
  }

  const labelSelectorView = new LabelsSelectorView({
    checkAll,
    checkMap,
    i18n,
    dataContextManager,
    cacheService,
    jsdata,
    isForVNext,
    edm,
    scope,
    customerLevelLabelEnabled,
    isManagementRequest,
    gridName,
    useSubMenu,
  });

  if (isForVNext) {
    // AS remove it directly from label not work, remove it here for now
    // TODO: after new UI GA, remove this from label view
    labelSelectorView.$el.removeClass('dropdown-menu');

    // add this for hover experience
    let cycleStatusClass = 'tri-state-selector';
    if (isShortCycle) {
      cycleStatusClass = 'two-state-selector';
    }

    labelSelectorView.$el.addClass(cycleStatusClass);
  }

  labelSelectorView.on('apply', updateAssociation({
    grid,
    selectionInfo,
    LabelAssociationView,
    isForVNext,
    toolbarEditor,
    toolbar,
    disableDeselectAll,
    isManagementRequest,
    gridName,
  }));
  labelSelectorView.on('create', onCreate({
    grid,
    selectionInfo,
    i18n,
    i18nErrors,
    LabelAssociationView,
    s$label,
    instrumentation,
    jsdata,
    isForVNext,
    toolbarEditor,
    toolbar,
    disableDeselectAll,
    edm,
    scope,
    customerLevelLabelEnabled,
    isManagementRequest,
    gridName,
  }));
  labelSelectorView.on('manage', () => { navigateToManagePage(router); });

  if (perfMarker) {
    labelSelectorView.on('request:data:beginning', () => {
      perfMarker.willFetchData();
    });
    labelSelectorView.on('request:data:finished', () => {
      perfMarker.willRender();
    });
    labelSelectorView.on('load:data', () => {
      if (!perfMarker.isDone) {
        perfMarker.done();
      }
    });
  }

  if (isForVNext) {
    const LabelSelectorViewWithFocusLock = focusLockInstanceDecorator(labelSelectorView, {
      focusFirstItemOnMount: true,
    }, {
      managed: true,
    });

    return new LabelSelectorViewWithFocusLock();
  }

  return labelSelectorView;
}

